import type { FilterRuleModel } from '@/shared/generated'

import { format, startOfMonth } from 'date-fns'
import { filter, isNil, map } from 'lodash/fp'

import { useAiBIAnalyticQuery, useMetricsQuery } from '@/entities/bi'

import { Skeleton } from '@/shared/components/ui/skeleton'
import { DateFormat } from '@/shared/tools/dates'

import { SalesFactorsImpactChart } from './SalesFactorsImpactChart'
import { SalesFactorsImpactEmptyState } from './SalesFactorsImpactEmptyState'
import { SalesFactorsImpactWidgetErrorState } from './SalesFactorsImpactErrorState'

export default function SalesFactorsImpactWidget({
	filters,
	isLoading: isDataLoading,
}: {
	filters: FilterRuleModel[]
	isLoading?: boolean
}) {
	const { data: metrics } = useMetricsQuery<string[]>({
		select: (data) => map('name', filter({ type: 'ai_importance' }, data)),
	})
	const commonQueryParams = {
		label: 'SalesFactorsImpact',

		/**last month by default*/
		start_date: format(startOfMonth(new Date()), DateFormat.system),
		end_date: format(new Date(), DateFormat.system),

		skip_dates: true,
		metrics: metrics as string[],
		dimensions: [],
		date_aggregation: null,
		filters,
	}
	const {
		data: biQuery,
		isLoading,
		isError,
	} = useAiBIAnalyticQuery(commonQueryParams, {
		enabled: !!metrics?.length,
	})
	const chartData = (biQuery?.data?.[0] || []).map((value, i) => ({
		metric: metrics![i],
		value,
	}))
	const isEmpty = biQuery?.data?.[0].every(isNil)

	return (
		<div className='relative flex flex-auto flex-col'>
			{isError && <SalesFactorsImpactWidgetErrorState />}
			{isLoading || isDataLoading ? (
				<LoadingState />
			) : isEmpty ? (
				<SalesFactorsImpactEmptyState />
			) : (
				<SalesFactorsImpactChart data={chartData} />
			)}
		</div>
	)
}

function LoadingState() {
	return (
		<div className='mb-3 space-y-2'>
			<Skeleton className='h-5 w-full rounded-lg' />
			<Skeleton className='h-8 w-full rounded-lg' />
			<Skeleton className='h-64 w-full rounded-lg' />
		</div>
	)
}
